import type { KorintAPIError } from './errors';

export type ErrorDictionary<E extends KorintAPIError = KorintAPIError> = {
  [code in E['payload']['code']]?: (
    p: E['payload'] & {
      code: code;
    },
  ) => ForUserError | undefined;
};

type UnknownError = {
  status: 'unknown';
  payload: string;
};

export type I18nResolver = {
  exists: (key: string) => boolean;
  t: (key: string | string[], options?: Record<string, unknown>) => string;
};

export type ErrorTranslatorConfig = {
  dict: ErrorDictionary;
  defaultErrorMessage: ForUserError;
  errorCodesToIgnore?: string[];
  i18n?: I18nResolver;
};

export type ForUserError = {
  userError: string;
  userHelp: string;
  response?: Record<string, unknown>;
};

/**
 * Resolves a Korint error payload to a user-facing message: tenant dict
 * handler first, i18n shortcut (`formErrors.<CODE>.title/description`) second,
 * `defaultErrorMessage` last. Returns undefined for codes in `errorCodesToIgnore`.
 */
export const resolveKorintErrorMessage = (
  payload: Exclude<KorintAPIError, UnknownError>['payload'],
  { dict, defaultErrorMessage, errorCodesToIgnore = [], i18n }: ErrorTranslatorConfig,
): ForUserError | undefined => {
  const code = payload.code;
  if (errorCodesToIgnore.includes(code)) {
    return undefined;
  }
  const translator = dict[code] as ((e: typeof payload) => ForUserError | undefined) | undefined;
  return (
    translator?.(payload) ??
    resolveI18nErrorMessage(i18n, code, payload.details) ??
    defaultErrorMessage
  );
};

// Same lookup order as the funnel forms, so the toast names the field the way the form does.
const fieldLabel = (i18n: I18nResolver, key: string): string =>
  i18n.t([`intermediaryInformationCard.fields.${key}`, `fields.${key}.label`], {
    defaultValue: key,
  });

/**
 * Backend validation errors carry their offending fields as `details.errors: [{ key }]`.
 * i18next cannot interpolate an array of objects, so expose the field labels as a joined
 * `{{fields}}` string — that name is what lets a message tell the user what to fix.
 */
const withInterpolatableFields = (
  i18n: I18nResolver,
  details: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  const errors = details?.errors;
  if (!Array.isArray(errors)) {
    return details;
  }
  const fields = errors
    .map(error => error?.key)
    .filter((key): key is string => typeof key === 'string')
    .map(key => fieldLabel(i18n, key))
    .join(', ');
  return { ...details, fields };
};

export const resolveI18nErrorMessage = (
  i18n: I18nResolver | undefined,
  code: string,
  details: Record<string, unknown> | undefined,
): ForUserError | undefined => {
  if (!i18n?.exists(`formErrors.${code}.title`)) {
    return undefined;
  }
  const interpolation = withInterpolatableFields(i18n, details);
  return {
    userError: i18n.t(`formErrors.${code}.title`, interpolation),
    userHelp: i18n.t(`formErrors.${code}.description`, interpolation),
  };
};

export const isKorintError = (e: unknown): e is KorintAPIError => {
  return typeof e === 'object' && e !== null && 'status' in e && typeof e.status === 'number';
};
